/**
 * Hesap Al — masanın açık siparişleri için ödeme alma sayfası (modal).
 *
 * QR menüdeki PaymentModal'ın POS karşılığı: orada müşteri sadece garson
 * çağırır; burada garson gerçek ödemeyi tahsil eder.
 *
 * Ödeme yöntemi davranışı:
 *  - NAKİT      → garson tutarı elle/hızlı tuşla girer (üstü para hesabı).
 *  - KART / QR  → tutar ELLE GİRİLMEZ. Kalan tutar PAX terminaline gönderilir
 *                 (paymentTerminalAdapter.charge); onaylanan tutar cihazdan
 *                 döner. Terminal APPROVED dönünce o tutar `tender` ile
 *                 kaydedilir (report-result tek başına Payment yaratmaz).
 *  - KARIŞIK    → garson nakit kısmını girer, KALAN tutar terminale gider.
 *
 * Akış:
 *  1. Sipariş kapsamı  — "Hepsi birlikte" (varsayılan) ya da "Seçili siparişler"
 *  2. Ödeme şekli      — "Tek ödeme" ya da "Parçalı (bölüşmeli)"
 *  3. Onayla   — kapsamdaki siparişler tahsil edilir, masa kapanır
 */
import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Divider,
  Checkbox,
  ActionIcon,
  Badge,
  NumberInput,
  SegmentedControl,
  Progress,
  Alert,
  Select,
  Card,
} from "@mantine/core";
import {
  IconPlus,
  IconMinus,
  IconCash,
  IconCreditCardPay,
  IconQrcode,
  IconAlertCircle,
  IconDeviceMobileCheck,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useTenderOrder, useResource, useTenderSession } from "../api";
import { paymentTerminalAdapter } from "../hardware";

function formatTRY(minor: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
  }).format((minor || 0) / 100);
}

type OrderLine = {
  id?: string;
  product_name: string;
  quantity: number;
  line_total_minor?: number;
  paid_amount_minor?: number;
  // İkram/iade edilmiş porsiyonlar ödenemez (serbest porsiyondan düşülür) —
  // Hesap Aktar'daki freeQty kuralıyla aynı.
  comp_quantity_approved?: number;
  returned_quantity_approved?: number;
};

type Order = {
  id: string;
  total_minor: number;
  source?: string;
  waiter_name?: string;
  // Sipariş satırları — "Seçili siparişler" listesinde içerik gösterimi için
  lines?: OrderLine[];
};

export function PaymentSheet({
  opened,
  onClose,
  orders,
  onPaid,
  sessionId,
}: {
  opened: boolean;
  onClose: () => void;
  orders: Order[];
  onPaid: () => void;
  // Yeni Adisyon (TableSession) entegrasyonu için: verilirse Hesap Al
  // butonu sezişlikle tek HTTP isteğiyle /table-sessions/{id}/tender/
  // endpoint'ine gider — Check'ler otomatik oluşur, ÖKC fiş Check
  // seviyesinde tetiklenir. Verilmezse eski Order-bazlı tender flow
  // korunur (geriye uyumlu).
  sessionId?: string | null;
}) {
  const tender = useTenderOrder();
  // TODO(adisyon-faz2): sessionId verildiğinde toplu tender_session
  // endpoint'ine geç — şu an PaymentSheet hâlâ Order-bazlı tender çağırıyor.
  // Backend tender_order Session'ı otomatik kapatıyor (Order CLOSED → Session
  // kontrol → CLOSED). Dolayısıyla mevcut akış işliyor; sadece Check kayıtları
  // tek HTTP isteğiyle değil, N adet Order tender'ı sonunda topluca
  // oluşturulması gerekiyor. ÖKC fiş trigger'ı şimdilik Order CLOSED'da da
  // çalışacak (Adım 5).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _tenderSession = useTenderSession();
  void _tenderSession; // ileride kullanılacak — şimdilik prop alındı, davranış değişmedi
  void sessionId;

  // Kayıtlı PAX/Verifone vb. ödeme terminalleri
  const { data: devicesData } = useResource<any>("hardware-devices", {
    filters: { device_type: "PAYMENT_TERMINAL", is_active: true },
  });
  const terminals: any[] = devicesData?.results || devicesData || [];

  // --- 1. Sipariş kapsamı ---
  const [scope, setScope] = useState<"all" | "pick">("all");
  const [picked, setPicked] = useState<string[]>([]);

  // --- 2. Ödeme şekli ---
  // - single: tüm tutar tek seferde
  // - split: N kişiye eşit böl (parçalar peyderpey)
  // - lines: müşteri sadece kendi yediği satırları öder (checkbox)
  const [mode, setMode] = useState<"single" | "split" | "lines">("single");

  // "Satır seç" modunda işaretli line ID'leri
  const [paidLineIds, setPaidLineIds] = useState<string[]>([]);

  // Satır seç + çok porsiyonlu satırda: {lineId: ödenecek porsiyon}.
  // Belirtilmeyen satır = serbest porsiyonun tamamı (eski davranış).
  const [lineQtyById, setLineQtyById] = useState<Record<string, number | string>>({});

  // Nakit elle girilir (TL). Kart/QR terminalden gelir — input yok.
  const [cash, setCash] = useState<number>(0);

  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [installments, setInstallments] = useState<number>(1);
  const [charging, setCharging] = useState(false);

  // Parçalı
  const [parts, setParts] = useState(2);
  const [paidParts, setPaidParts] = useState(0);

  // Modal her açıldığında durumu sıfırla.
  // KRİTİK: deps array'inden `orders` ÇIKARILDI. orders parent'tan her
  // render'da YENİ array referansıyla geliyor (openOrders.filter(...)),
  // bu da WS push veya 30 sn polling sonrası effect'i tetikleyip mode'u
  // "single"a resetliyordu — kullanıcı "Satır Seç" tıklasa bile birkaç
  // saniye sonra otomatik "Tek Ödeme"ye düşüyordu.
  // Modal açılışında (false → true) durum sıfırlanır; o sırada orders
  // current değer üzerinden okunur. ESLint react-hooks/exhaustive-deps
  // burada kasıtlı olarak es geçilir.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (opened) {
      setScope("all");
      setPicked(orders.map((o) => o.id));
      setMode("single");
      setCash(0);
      setInstallments(1);
      setParts(2);
      setPaidParts(0);
      setPaidLineIds([]);
      setLineQtyById({});
    }
  }, [opened]);

  // İkram/iade edilmiş porsiyonlar ödenemez → serbest porsiyon = qty − comp − iade.
  // (Hesap Aktar'daki freeQty ile aynı kural.)
  const freeQty = (ln: OrderLine): number =>
    Math.max(
      0,
      ln.quantity -
        ((ln.comp_quantity_approved ?? 0) + (ln.returned_quantity_approved ?? 0)),
    );

  // İlk terminal otomatik seçili
  useEffect(() => {
    if (!deviceId && terminals.length > 0) setDeviceId(terminals[0].id);
  }, [terminals, deviceId]);

  // Kapsamdaki siparişler + toplam
  const scopedOrders = useMemo(() => {
    if (orders.length <= 1 || scope === "all") return orders;
    return orders.filter((o) => picked.includes(o.id));
  }, [orders, scope, picked]);

  // Tüm açık (ödenmemiş) satırlar — "Satır Seç" modu için flat liste.
  // Her satıra hangi siparişe ait olduğu meta'sı eklenir (backend tender
  // çağrısında order ID + line ID birlikte gerekli).
  const allOpenLines = useMemo(() => {
    const out: Array<{ orderId: string; line: OrderLine; index: number }> = [];
    for (const o of scopedOrders) {
      let i = 0;
      for (const ln of o.lines || []) {
        if (!ln.id) continue;
        const lineTotal = ln.line_total_minor ?? 0;
        const paid = ln.paid_amount_minor ?? 0;
        // Tam ödenmiş satırı gizleme; "✓ Ödendi" rozetiyle disabled gösterilir
        out.push({ orderId: o.id, line: { ...ln, line_total_minor: lineTotal, paid_amount_minor: paid }, index: i });
        i++;
      }
    }
    return out;
  }, [scopedOrders]);

  // Bir satır için bu ödemede tahsil edilecek tutar. Kısmi-porsiyon seçiliyse
  // line_total × seçili_qty / quantity; tüm serbest porsiyon seçiliyse satırın
  // TAM kalanı (kuruş kaybı olmasın — backend ile aynı mantık).
  const linePayMinor = (ln: OrderLine): number => {
    const full = ln.line_total_minor ?? 0;
    const paid = ln.paid_amount_minor ?? 0;
    const free = freeQty(ln);
    const q = Number(lineQtyById[ln.id!] ?? free);
    if (q >= free) return Math.max(0, full - paid);
    return Math.round((full * q) / (ln.quantity || 1));
  };

  // Bu ödeme sonrası satır TAM kapanır mı? (önceki + bu ödeme >= satır toplamı)
  // Kısmi-porsiyon seçildiyse (q < free) satır açık kalır → false.
  const lineFullyClosedAfter = (ln: OrderLine): boolean => {
    const full = ln.line_total_minor ?? 0;
    if (full <= 0) return true;
    return (ln.paid_amount_minor ?? 0) + linePayMinor(ln) >= full;
  };

  // mode === "lines" ise totalMinor = seçili line'ların (kısmi-porsiyon dahil)
  // tahsil edilecek tutarı
  const linesSelectedTotal = useMemo(() => {
    if (mode !== "lines") return 0;
    return allOpenLines
      .filter((x) => paidLineIds.includes(x.line.id!))
      .reduce((s, x) => s + linePayMinor(x.line), 0);
    // linePayMinor lineQtyById'ye bağlı — dep listesine ekli
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, allOpenLines, paidLineIds, lineQtyById]);

  const ordersTotal = scopedOrders.reduce((s, o) => s + (o.total_minor || 0), 0);
  const totalMinor = mode === "lines" ? linesSelectedTotal : ordersTotal;
  const cashMinor = Math.round(cash * 100);
  const remainingMinor = Math.max(0, totalMinor - cashMinor);

  const perPartMinor = parts > 0 ? Math.floor(totalMinor / parts) : totalMinor;

  /**
   * Kapsamdaki siparişlere bir tutarı sırayla tender olarak dağıtır.
   * Her order kendi toplamı kadar, son order kalan tutarı alır — böylece
   * çoklu siparişte de tam kapanır.
   *
   * Satır seçimli ödemede (`mode==="lines"`): her order için yalnız o
   * order'a ait seçili line'ların tutarı tender olarak gider; backend
   * paid_line_ids ile o satırları fully paid işaretler.
   */
  async function distributeTender(tenderType: string, amountMinor: number) {
    if (mode === "lines") {
      // OrderId → seçili line ID'leri + line tutarları + kısmi-porsiyon haritası
      const byOrder = new Map<
        string,
        { ids: string[]; total: number; qtys: Record<string, number> }
      >();
      for (const x of allOpenLines) {
        if (!paidLineIds.includes(x.line.id!)) continue;
        const entry =
          byOrder.get(x.orderId) || { ids: [], total: 0, qtys: {} };
        entry.ids.push(x.line.id!);
        entry.total += linePayMinor(x.line);
        // Kısmi-porsiyon: serbest porsiyonun tamamından AZ seçildiyse gönder.
        const free = freeQty(x.line);
        const q = Number(lineQtyById[x.line.id!] ?? free);
        if (q > 0 && q < free) entry.qtys[x.line.id!] = q;
        byOrder.set(x.orderId, entry);
      }
      // Her order'a kendi seçili line'larının (kısmi) tutarı kadar tender at
      for (const [orderId, info] of byOrder) {
        await tender.mutateAsync({
          orderId,
          tenders: [{ type: tenderType, amount_minor: info.total }],
          paid_line_ids: info.ids,
          ...(Object.keys(info.qtys).length
            ? { line_paid_quantities: info.qtys }
            : {}),
        });
      }
      return;
    }

    let left = amountMinor;
    for (const o of scopedOrders) {
      if (left <= 0) break;
      // total_minor=0 olan sipariş (boş/taslak) ödemeye dahil edilmez —
      // 0 TL tender backend'de "Order already fully paid" hatası verir.
      if (!o.total_minor || o.total_minor <= 0) continue;
      const portion = Math.min(left, o.total_minor);
      await tender.mutateAsync({
        orderId: o.id,
        tenders: [{ type: tenderType, amount_minor: portion }],
      });
      left -= portion;
    }
  }

  /**
   * Kart/QR — kalan tutarı PAX terminaline gönderir. Onaylanırsa o tutar +
   * (varsa) nakit kısmı tender olarak kaydedilir, masa kapanır.
   */
  async function chargeTerminal(tenderType: "CARD" | "QR_DIGITAL") {
    const amount = remainingMinor;
    if (amount <= 0) return;
    if (!deviceId) {
      notifications.show({
        message: "Önce bir ödeme terminali seçin",
        color: "red",
      });
      return;
    }
    if (scopedOrders.length === 0) {
      notifications.show({ message: "Ödenecek sipariş seçilmedi", color: "red" });
      return;
    }
    setCharging(true);
    try {
      // Terminal işlemini ilk sipariş üzerinden başlat (transaction kaydı için)
      const { result } = await paymentTerminalAdapter.charge({
        device_id: deviceId,
        order_id: scopedOrders[0].id,
        amount_minor: amount,
        installments,
      });
      if (result.status !== "APPROVED") {
        notifications.show({
          message: `Terminal: ${result.error_message || result.status}`,
          color: "red",
        });
        return;
      }
      // Terminal onayladı → tutarı tender olarak kaydet
      await distributeTender(tenderType, amount);
      // Nakit kısmı da varsa onu da kaydet
      if (cashMinor > 0) {
        await distributeTender("CASH", cashMinor);
      }
      notifications.show({
        message: `${formatTRY(amount)} ${
          tenderType === "CARD" ? "kart" : "QR"
        } ile alındı${
          result.card_last_4 ? ` (**** ${result.card_last_4})` : ""
        }`,
        color: "green",
      });
      // Satır seçimli: bu ödeme bazı satırları kapadı; kalan satırlar varsa
      // modal açık kalsın, diğer kişiler için seçime devam edilebilir.
      // Kısmi-porsiyon: seçili ama tam kapanmamış satır da "açık" sayılır.
      if (mode === "lines") {
        const remainingOpenLines = allOpenLines.filter((x) => {
          const selected = paidLineIds.includes(x.line.id!);
          const total = x.line.line_total_minor ?? 0;
          const paid = x.line.paid_amount_minor ?? 0;
          if (selected) return !lineFullyClosedAfter(x.line);
          return paid < total;
        });
        if (remainingOpenLines.length === 0) {
          onPaid();
        } else {
          setPaidLineIds([]);
          setLineQtyById({});
          setCash(0);
        }
      } else {
        onPaid();
      }
    } catch (e: any) {
      notifications.show({
        message: e?.message || e?.response?.data?.detail || "Terminal hatası",
        color: "red",
      });
    } finally {
      setCharging(false);
    }
  }

  /** Sadece nakit ile tam ödeme (kart/QR kullanılmadı). */
  async function finishCashOnly() {
    if (cashMinor < totalMinor) {
      notifications.show({ message: "Nakit tutar eksik", color: "red" });
      return;
    }
    if (scopedOrders.length === 0) {
      notifications.show({ message: "Ödenecek sipariş seçilmedi", color: "red" });
      return;
    }
    if (mode === "lines" && paidLineIds.length === 0) {
      notifications.show({
        message: "En az bir satır seçin",
        color: "red",
      });
      return;
    }
    try {
      // Nakitte üstü verilir — siparişlere tam tutar kadar tender yazılır
      await distributeTender("CASH", totalMinor);
      // Satır seçimli modda: bu ödeme bazı satırları kapadı ama tüm sipariş
      // kapanmamış olabilir. onPaid'i sadece tüm açık line'lar bittiyse çağır
      // (modal kapanmasın; diğer kişiler için satır seçmeye devam edilebilir).
      // Tek ödeme / eşit parçalı → her ödeme tüm siparişi kapatır → onPaid.
      if (mode === "lines") {
        // Bir satır "açık kaldı" sayılır: seçilmediyse VE henüz tam ödenmemiş,
        // YA DA seçildi ama bu ödemeyle tam kapanmadı (kısmi-porsiyon).
        const remainingOpenLines = allOpenLines.filter((x) => {
          const selected = paidLineIds.includes(x.line.id!);
          const total = x.line.line_total_minor ?? 0;
          const paid = x.line.paid_amount_minor ?? 0;
          if (selected) return !lineFullyClosedAfter(x.line);
          return paid < total;
        });
        if (remainingOpenLines.length === 0) {
          onPaid();
        } else {
          // Seçimi temizle, kullanıcı bir sonraki kişi için yeni satır seçsin
          setPaidLineIds([]);
          setLineQtyById({});
          setCash(0);
          notifications.show({
            message: `${formatTRY(cashMinor)} alındı. Kalan satırlar için seçime devam edin.`,
            color: "green",
            autoClose: 3000,
          });
        }
      } else {
        onPaid();
      }
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Ödeme başarısız",
        color: "red",
      });
    }
  }

  /** Parçalı: bir parça tahsil edildi (nakit anında / kart terminalden). */
  async function collectPart(via: "CASH" | "CARD") {
    const isLast = paidParts + 1 >= parts;
    try {
      if (via === "CARD") {
        if (!deviceId) {
          notifications.show({
            message: "Önce bir ödeme terminali seçin",
            color: "red",
          });
          return;
        }
        setCharging(true);
        const { result } = await paymentTerminalAdapter.charge({
          device_id: deviceId,
          order_id: scopedOrders[0].id,
          amount_minor: perPartMinor,
          installments: 1,
        });
        if (result.status !== "APPROVED") {
          notifications.show({
            message: `Terminal: ${result.error_message || result.status}`,
            color: "red",
          });
          return;
        }
      }
      if (isLast) {
        // Parçalar fiziksel bölüşmedir (garson her parçayı elden alır).
        // Backend'e gönderirken HER SİPARİŞ kendi tam tutarıyla kapatılmalı
        // — `tender_order` sipariş bazında çalışır, eksik tutar reddedilir.
        // Son parçada tüm siparişleri tam toplamla tek seferde kapatırız.
        await distributeTender(
          via === "CARD" ? "CARD" : "CASH",
          totalMinor,
        );
        onPaid();
      } else {
        setPaidParts((p) => p + 1);
        notifications.show({
          message: `${paidParts + 1}/${parts} parça alındı (${formatTRY(
            perPartMinor,
          )})`,
          color: "green",
          autoClose: 1500,
        });
      }
    } catch (e: any) {
      notifications.show({
        message: e?.message || e?.response?.data?.detail || "Ödeme başarısız",
        color: "red",
      });
    } finally {
      setCharging(false);
    }
  }

  const multi = orders.length > 1;
  const noTerminal = terminals.length === 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Hesap Al"
      size="md"
      radius="md"
    >
      <Stack gap="md">
        {/* Toplam */}
        <Group justify="space-between">
          <Text c="dimmed">Ödenecek Tutar</Text>
          <Text fw={800} size="xl" c="blue">
            {formatTRY(totalMinor)}
          </Text>
        </Group>

        {/* --- 1. Sipariş kapsamı (sadece çoklu siparişte) --- */}
        {multi && (
          <>
            <Divider label="Sipariş kapsamı" labelPosition="center" />
            <SegmentedControl
              fullWidth
              value={scope}
              onChange={(v) => {
                const next = v as "all" | "pick";
                setScope(next);
                // "Seçili siparişler"e geçince seçim boş başlasın —
                // kullanıcı hangilerini ödeyeceğini bilinçli işaretler.
                setPicked(next === "pick" ? [] : orders.map((o) => o.id));
              }}
              data={[
                { label: "Hepsi birlikte", value: "all" },
                { label: "Seçili siparişler", value: "pick" },
              ]}
            />
            {scope === "pick" && (
              <Stack gap={8}>
                {orders.map((o, idx) => {
                  const isPicked = picked.includes(o.id);
                  return (
                    <Card
                      key={o.id}
                      padding="xs"
                      radius="md"
                      withBorder
                      style={{
                        cursor: "pointer",
                        borderColor: isPicked
                          ? "var(--mantine-color-blue-5)"
                          : undefined,
                        background: isPicked
                          ? "var(--mantine-color-blue-0)"
                          : undefined,
                      }}
                      onClick={() => {
                        setPicked(
                          isPicked
                            ? picked.filter((x) => x !== o.id)
                            : [...picked, o.id],
                        );
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap" mb={4}>
                        <Group gap={6} wrap="nowrap">
                          <Checkbox
                            checked={isPicked}
                            onChange={() => {}}
                            tabIndex={-1}
                            styles={{ input: { cursor: "pointer" } }}
                          />
                          <Text size="sm" fw={600}>
                            #{idx + 1} ·{" "}
                            {o.source === "QR"
                              ? "QR Müşteri"
                              : o.waiter_name || "POS"}
                          </Text>
                        </Group>
                        <Text size="sm" fw={700}>
                          {formatTRY(o.total_minor)}
                        </Text>
                      </Group>
                      {/* Sipariş içeriği — hangi ürünler */}
                      <Stack gap={1} pl={28}>
                        {(o.lines || []).map((ln, i) => (
                          <Group
                            key={ln.id || i}
                            justify="space-between"
                            wrap="nowrap"
                          >
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {ln.quantity}× {ln.product_name}
                            </Text>
                            {ln.line_total_minor != null && (
                              <Text size="xs" c="dimmed">
                                {formatTRY(ln.line_total_minor)}
                              </Text>
                            )}
                          </Group>
                        ))}
                        {(!o.lines || o.lines.length === 0) && (
                          <Text size="xs" c="dimmed">
                            Sipariş içeriği yok
                          </Text>
                        )}
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </>
        )}

        {/* --- 2. Ödeme şekli --- */}
        <Divider label="Ödeme şekli" labelPosition="center" />
        <SegmentedControl
          fullWidth
          value={mode}
          onChange={(v) => setMode(v as "single" | "split" | "lines")}
          data={[
            { label: "Tek Ödeme", value: "single" },
            { label: "Eşit Parçalı", value: "split" },
            { label: "Satır Seç", value: "lines" },
          ]}
        />

        {/* --- SATIR SEÇ MODU --- müşteri "ben sadece tostumu öderim" der */}
        {mode === "lines" && (
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              Bu kişinin ödeyeceği satırları seçin. Seçilenler tam tutarıyla
              ödenir; kalan satırlar masa açık kalır.
            </Text>
            {allOpenLines.length === 0 ? (
              <Alert color="gray" icon={<IconAlertCircle size={18} />}>
                Ödenebilir satır yok.
              </Alert>
            ) : (
              <Stack gap={4}>
                {/* Toplu seçim — uzun siparişlerde hız kazandırır.
                    "Tümünü Seç" sadece henüz ödenmemiş line'ları işaretler. */}
                {(() => {
                  const payable = allOpenLines.filter(
                    (x) =>
                      (x.line.paid_amount_minor ?? 0) <
                      (x.line.line_total_minor ?? 0),
                  );
                  return (
                    <Group justify="space-between">
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        onClick={() =>
                          setPaidLineIds(payable.map((x) => x.line.id!))
                        }
                        disabled={paidLineIds.length === payable.length}
                      >
                        ✓ Tümünü Seç ({payable.length})
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => setPaidLineIds([])}
                        disabled={paidLineIds.length === 0}
                      >
                        Temizle
                      </Button>
                    </Group>
                  );
                })()}
                {allOpenLines.map((x) => {
                  const ln = x.line;
                  const lineTotal = ln.line_total_minor ?? 0;
                  const isPaid =
                    (ln.paid_amount_minor ?? 0) >= lineTotal && lineTotal > 0;
                  const isChecked = paidLineIds.includes(ln.id!);
                  return (
                    <Card
                      key={`${x.orderId}-${ln.id}`}
                      padding="xs"
                      radius="sm"
                      withBorder
                      style={{
                        cursor: isPaid ? "not-allowed" : "pointer",
                        opacity: isPaid ? 0.55 : 1,
                        borderColor: isChecked
                          ? "var(--mantine-color-blue-5)"
                          : undefined,
                        background: isChecked
                          ? "var(--mantine-color-blue-0)"
                          : undefined,
                      }}
                      onClick={() => {
                        if (isPaid) return;
                        setPaidLineIds((prev) =>
                          isChecked
                            ? prev.filter((id) => id !== ln.id)
                            : [...prev, ln.id!],
                        );
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap={8} wrap="nowrap">
                          <Checkbox
                            checked={isChecked}
                            disabled={isPaid}
                            onChange={() => {}}
                            tabIndex={-1}
                            styles={{ input: { cursor: "pointer" } }}
                          />
                          <Text size="sm" fw={500}>
                            {ln.quantity}× {ln.product_name}
                          </Text>
                          {isPaid && (
                            <Badge size="xs" color="green">
                              ✓ Ödendi
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" fw={600}>
                          {formatTRY(lineTotal)}
                        </Text>
                      </Group>
                      {/* Kısmi porsiyon — seçili + birden fazla SERBEST porsiyon
                          varsa. "3× Antrikot'un kaçını öde?" İkram/iade porsiyon
                          serbest sayımdan düşülür (ödenemez). */}
                      {isChecked && freeQty(ln) > 1 && (
                        <Group
                          gap={8}
                          mt={6}
                          wrap="nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                            Kaç porsiyon ödenecek?
                          </Text>
                          <NumberInput
                            size="xs"
                            w={90}
                            min={1}
                            max={freeQty(ln)}
                            allowDecimal={false}
                            value={lineQtyById[ln.id!] ?? freeQty(ln)}
                            onChange={(v) =>
                              setLineQtyById((prev) => ({ ...prev, [ln.id!]: v }))
                            }
                          />
                          <Text size="xs" c="dimmed">
                            / {freeQty(ln)} · {formatTRY(linePayMinor(ln))}
                          </Text>
                        </Group>
                      )}
                    </Card>
                  );
                })}
              </Stack>
            )}
            <Group justify="space-between" mt="xs">
              <Text c="dimmed" size="sm">
                Seçili tutar
              </Text>
              <Text fw={700} c="blue">
                {formatTRY(linesSelectedTotal)}
              </Text>
            </Group>
          </Stack>
        )}

        {totalMinor === 0 && (
          <Alert color="orange" icon={<IconAlertCircle size={18} />}>
            Ödenecek sipariş seçilmedi.
          </Alert>
        )}

        {/* Terminal seçimi — kart/QR için ortak */}
        {totalMinor > 0 &&
          (noTerminal ? (
            <Alert color="orange" icon={<IconAlertCircle size={18} />}>
              Kayıtlı ödeme terminali yok. Kart/QR çekmek için yöneticinizden
              bir PAX/Verifone cihazı tanımlamasını isteyin.
            </Alert>
          ) : (
            <Group grow>
              <Select
                label="Ödeme terminali"
                placeholder="Terminal seç"
                data={terminals.map((t) => ({
                  value: t.id,
                  label: `${t.name}${t.vendor ? ` (${t.vendor})` : ""}`,
                }))}
                value={deviceId}
                onChange={setDeviceId}
              />
              <NumberInput
                label="Taksit"
                value={installments}
                onChange={(v) => setInstallments(Number(v) || 1)}
                min={1}
                max={12}
              />
            </Group>
          ))}

        {/* --- TEK ÖDEME / SATIR SEÇ — ortak nakit + kart akışı --- */}
        {(mode === "single" || mode === "lines") && totalMinor > 0 && (
          <Stack gap="sm">
            {/* Nakit — elle girilir */}
            <NumberInput
              label={
                <Group gap={6}>
                  <IconCash size={16} /> Nakit (elle)
                </Group>
              }
              description="Müşteri nakit veriyorsa tutarı girin. Kalanı kart/QR ile alınır."
              value={cash}
              onChange={(v) => setCash(Number(v) || 0)}
              min={0}
              decimalScale={2}
              thousandSeparator=","
              suffix=" TL"
              size="md"
            />
            <Group gap="xs">
              {[50, 100, 200, totalMinor / 100].map((amt) => (
                <Button
                  key={amt}
                  size="xs"
                  variant="default"
                  onClick={() => setCash(amt)}
                >
                  {amt.toFixed(0)} TL
                </Button>
              ))}
            </Group>

            <Divider />

            {/* Durum özeti */}
            <Group justify="space-between">
              <Text c="dimmed">Nakit girilen</Text>
              <Text fw={600}>{formatTRY(cashMinor)}</Text>
            </Group>
            <Group justify="space-between">
              <Text fw={700} c={remainingMinor > 0 ? "red" : "green"}>
                {remainingMinor > 0 ? "Kart/QR ile alınacak" : "Tamamlandı"}
              </Text>
              <Text fw={700} c={remainingMinor > 0 ? "red" : "green"}>
                {formatTRY(remainingMinor)}
              </Text>
            </Group>

            {/* Kart / QR — terminale gönder (tutar elle girilmez) */}
            {remainingMinor > 0 ? (
              <Card padding="sm" radius="md" withBorder bg="blue.0">
                <Stack gap="xs">
                  <Group gap={6}>
                    <IconDeviceMobileCheck size={18} />
                    <Text fw={600} size="sm">
                      Kalan {formatTRY(remainingMinor)} — terminale gönder
                    </Text>
                  </Group>
                  <Group grow>
                    <Button
                      leftSection={<IconCreditCardPay size={18} />}
                      loading={charging}
                      disabled={noTerminal}
                      onClick={() => chargeTerminal("CARD")}
                    >
                      Kartla Çek
                    </Button>
                    <Button
                      variant="light"
                      leftSection={<IconQrcode size={18} />}
                      loading={charging}
                      disabled={noTerminal}
                      onClick={() => chargeTerminal("QR_DIGITAL")}
                    >
                      QR ile Çek
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Tutar terminalde onaylanır — elle giriş yok.
                  </Text>
                </Stack>
              </Card>
            ) : (
              // Tam nakit ödeme — terminale gerek yok
              <Button
                size="lg"
                fullWidth
                leftSection={<IconCash size={20} />}
                loading={tender.isPending}
                onClick={finishCashOnly}
              >
                Nakit Ödemeyi Tamamla
              </Button>
            )}
          </Stack>
        )}

        {/* --- PARÇALI ÖDEME --- */}
        {mode === "split" && totalMinor > 0 && (
          <Stack gap="sm">
            <Text size="sm" fw={600}>
              Kaç parçaya bölünsün?
            </Text>
            <Group gap="xs" justify="center">
              <ActionIcon
                size="lg"
                variant="default"
                disabled={paidParts > 0}
                onClick={() => setParts((p) => Math.max(2, p - 1))}
              >
                <IconMinus size={16} />
              </ActionIcon>
              <Badge size="xl" variant="filled">
                {parts} kişi
              </Badge>
              <ActionIcon
                size="lg"
                variant="default"
                disabled={paidParts > 0}
                onClick={() => setParts((p) => Math.min(20, p + 1))}
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>
            <Text size="sm" c="dimmed" ta="center">
              Kişi başı <strong>{formatTRY(perPartMinor)}</strong>
            </Text>

            <Progress value={(paidParts / parts) * 100} size="lg" radius="xl" />
            <Text size="sm" ta="center" fw={600}>
              {paidParts} / {parts} parça alındı
            </Text>

            <Text size="xs" c="dimmed" ta="center">
              Sıradaki parçayı ({formatTRY(perPartMinor)}) tahsil edin:
            </Text>
            <Group grow>
              <Button
                variant="default"
                leftSection={<IconCash size={18} />}
                loading={tender.isPending}
                onClick={() => collectPart("CASH")}
              >
                Nakit Al
              </Button>
              <Button
                leftSection={<IconCreditCardPay size={18} />}
                loading={charging}
                disabled={noTerminal}
                onClick={() => collectPart("CARD")}
              >
                Kartla Çek
              </Button>
            </Group>
            {noTerminal && (
              <Text size="xs" c="orange.7" ta="center">
                Kart çekmek için terminal tanımlı değil — parçaları nakit alın.
              </Text>
            )}
            <Text size="xs" c="dimmed" ta="center">
              Son parça alınınca masa otomatik kapanır.
            </Text>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
